import { __decorate } from 'tslib';
import { unsafeCSS, css, LitElement, html, nothing } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { createChart } from 'lightweight-charts';
import { periodsWithTime, chartStyles, histogramScale, linearScale } from './tradingViewSimpleChart.customization.js';
import { PeriodEnum, ChartType } from './tradingViewSimpleChart.props.js';
import { formatUnixDate, chartDataToSeries, getCandlesticksTooltip, fmtPrice } from './tradingViewSimpleChart.utils.js';
import css_248z from './tradingViewSimpleChart.styles.css.js';
import '../ButtonedTabBar/buttonedTabBar.js';
import '../SwitchButton/switchButton.js';
import '../Icons/candlesticksIcon.js';
import '../Icons/volumeIcon.js';

/**
 * TradingView Simple Chart Component.
 *
 * @element ht-trading-view-simple-chart
 */
let HTTradingViewSimpleChart = class HTTradingViewSimpleChart extends LitElement {
    constructor() {
        super(...arguments);
        this.data = { data: [] };
        this.loading = false;
        this.selectedPeriod = PeriodEnum.day;
        this.showVolume = false;
        this.showControls = true;
        this.chartHeight = 400;
        this.translations = {
            date: 'Date',
            value: 'Value',
            volume: 'Volume',
            day: 'Day',
            week: 'Week',
            month: 'Month',
            year: 'Year',
            max: 'Max',
            open: 'Open',
            close: 'Close',
            high: 'High',
            low: 'Low',
        };
        this.chart = null;
        this.tooltip = null;
        this.candlestickSeries = null;
        this.volumeSeries = null;
        this.lineSeries = null;
        this.candlesticksEnabled = false;
        this.volumeEnabled = false;
        this.mode = this.searchDataTheme();
        this.availablePeriods = [
            {
                label: this.translations.day,
                period: PeriodEnum.day,
            },
            {
                label: this.translations.week,
                period: PeriodEnum.week,
            },
            {
                label: this.translations.month,
                period: PeriodEnum.month,
            },
            {
                label: this.translations.year,
                period: PeriodEnum.year,
            },
            {
                label: this.translations.max,
                period: PeriodEnum.max,
            },
        ];
    }
    firstUpdated() {
        if (!this.loading && this.data) {
            this.initChart();
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('loading') && !this.loading) {
            this.updateChart();
        }
        this.updateChart();
    }
    searchDataTheme() {
        var _a;
        const theme = document.querySelector('[data-ht-theme]');
        return (_a = theme === null || theme === void 0 ? void 0 : theme.getAttribute('data-ht-theme')) !== null && _a !== void 0 ? _a : 'light';
    }
    getChartContainer() {
        var _a;
        return ((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById('chartContainer')) || null;
    }
    updateSeriesVisibility() {
        var _a, _b, _c;
        (_a = this.volumeSeries) === null || _a === void 0 ? void 0 : _a.applyOptions({ visible: this.volumeEnabled });
        (_b = this.lineSeries) === null || _b === void 0 ? void 0 : _b.applyOptions({ visible: !this.candlesticksEnabled });
        (_c = this.candlestickSeries) === null || _c === void 0 ? void 0 : _c.applyOptions({ visible: this.candlesticksEnabled });
    }
    updateChartLocalization() {
        if (this.chart) {
            const isIntraDay = periodsWithTime.includes(this.selectedPeriod);
            this.chart.applyOptions({
                localization: {
                    timeFormatter: (time) => formatUnixDate(time, isIntraDay),
                },
            });
        }
    }
    updateChartData() {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!this.chart || !this.data)
            return;
        (_a = this.volumeSeries) === null || _a === void 0 ? void 0 : _a.setData(chartDataToSeries((_b = this.data) === null || _b === void 0 ? void 0 : _b.data, ChartType.histogram));
        (_c = this.lineSeries) === null || _c === void 0 ? void 0 : _c.setData(chartDataToSeries((_d = this.data) === null || _d === void 0 ? void 0 : _d.data, ChartType.simple));
        (_e = this.candlestickSeries) === null || _e === void 0 ? void 0 : _e.setData(chartDataToSeries((_f = this.data) === null || _f === void 0 ? void 0 : _f.data, ChartType.candlesticks));
        (_g = this.chart) === null || _g === void 0 ? void 0 : _g.timeScale().fitContent();
    }
    initChart() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        const container = this.getChartContainer();
        if (!container)
            return;
        this.mode = this.searchDataTheme();
        this.chart = createChart(container, {
            width: container.clientWidth,
            height: this.chartHeight - 60,
        });
        const styling = chartStyles(this.mode, this.selectedPeriod);
        (_a = this.chart) === null || _a === void 0 ? void 0 : _a.applyOptions(styling.chart);
        this.volumeSeries = (_b = this.chart) === null || _b === void 0 ? void 0 : _b.addHistogramSeries();
        (_c = this.volumeSeries) === null || _c === void 0 ? void 0 : _c.setData(chartDataToSeries((_d = this.data) === null || _d === void 0 ? void 0 : _d.data, ChartType.histogram));
        (_e = this.volumeSeries) === null || _e === void 0 ? void 0 : _e.applyOptions(styling.histogram);
        (_f = this.volumeSeries) === null || _f === void 0 ? void 0 : _f.priceScale().applyOptions(histogramScale);
        this.lineSeries = (_g = this.chart) === null || _g === void 0 ? void 0 : _g.addLineSeries();
        (_h = this.lineSeries) === null || _h === void 0 ? void 0 : _h.setData(chartDataToSeries((_j = this.data) === null || _j === void 0 ? void 0 : _j.data, ChartType.simple));
        (_k = this.lineSeries) === null || _k === void 0 ? void 0 : _k.applyOptions(styling.lineSeries);
        (_l = this.lineSeries) === null || _l === void 0 ? void 0 : _l.priceScale().applyOptions(linearScale);
        this.candlestickSeries = (_m = this.chart) === null || _m === void 0 ? void 0 : _m.addCandlestickSeries();
        (_o = this.candlestickSeries) === null || _o === void 0 ? void 0 : _o.setData(chartDataToSeries((_p = this.data) === null || _p === void 0 ? void 0 : _p.data, ChartType.candlesticks));
        (_q = this.candlestickSeries) === null || _q === void 0 ? void 0 : _q.applyOptions(styling.candleSeries);
        (_r = this.candlestickSeries) === null || _r === void 0 ? void 0 : _r.priceScale().applyOptions(linearScale);
        this.tooltip = document.createElement('div');
        this.tooltip.className = 'chartTooltip';
        container.appendChild(this.tooltip);
        (_s = this.chart) === null || _s === void 0 ? void 0 : _s.subscribeCrosshairMove((param) => this.handleTooltip(param));
        window.addEventListener('resize', () => this.resizeChart());
    }
    updateChart() {
        this.updateSeriesVisibility();
        this.updateChartLocalization();
        this.updateChartData();
    }
    resizeChart() {
        const container = this.getChartContainer();
        if (!this.chart || !container)
            return;
        this.chart.resize(container.clientWidth, this.chartHeight - 60);
    }
    handleTooltip(param) {
        if (!this.tooltip)
            return;
        if (!param.time || param.point.x < 0 || param.point.y < 0) {
            this.tooltip.style.display = 'none';
            return;
        }
        const price = param.seriesData.get(this.candlestickSeries);
        if (!price) {
            this.tooltip.style.display = 'none';
            return;
        }
        this.tooltip.style.display = 'block';
        const date = formatUnixDate(param.time, true);
        let tooltipContent = `<div>${this.translations.date}: ${date}</div>`;
        tooltipContent += this.candlesticksEnabled
            ? getCandlesticksTooltip(price, this.translations)
            : `<div>${this.translations.value}: ${fmtPrice(price.close)}</div>`;
        if (this.volumeEnabled) {
            const volume = param.seriesData.get(this.volumeSeries);
            tooltipContent += `<div>${this.translations.volume}: ${volume.value}</div>`;
        }
        this.tooltip.innerHTML = tooltipContent;
    }
    onPeriodChange(period) {
        this.selectedPeriod = period;
    }
    setCandlesticksEnabled(enabled) {
        this.candlesticksEnabled = enabled;
    }
    setVolumeEnabled(enabled) {
        this.volumeEnabled = enabled;
    }
    render() {
        var _a;
        return html `
      <div class="chart">
        ${this.loading
            ? html `<div>Loading...</div>`
            : html `
              <div id="chartContainer" class="chart__container"></div>
              ${this.showControls
                ? html `<div
                    class="chart__controls w-100 d-flex align-center justify-space-between pt-2"
                  >
                    <buttoned-tabs
                      .fullWidth=${true}
                      .size=${'large'}
                      .tabs=${(_a = this.availablePeriods) === null || _a === void 0 ? void 0 : _a.map((p) => ({
                    id: p === null || p === void 0 ? void 0 : p.period,
                    title: p === null || p === void 0 ? void 0 : p.label,
                }))}
                      .activeTab=${this.selectedPeriod}
                      @tab-change=${(e) => this.onPeriodChange(e.detail.activeTab)}
                      .loading=${false}
                    ></buttoned-tabs>
                    ${this.showVolume
                    ? html `
                          <div class="chart__options">
                            <ht-switch-button
                              .isPressed=${this.candlesticksEnabled}
                              @toggle=${() => this.setCandlesticksEnabled(!this.candlesticksEnabled)}
                            >
                              <candlesticks-icon
                                slot="left"
                                size="24"
                              ></candlesticks-icon>
                            </ht-switch-button>
                            <ht-switch-button
                              .isPressed=${this.volumeEnabled}
                              @toggle=${() => this.setVolumeEnabled(!this.volumeEnabled)}
                            >
                              <volume-icon slot="left" size="24"></volume-icon>
                            </ht-switch-button>
                          </div>
                        `
                    : ''}
                  </div>`
                : nothing}
            `}
      </div>
    `;
    }
};
HTTradingViewSimpleChart.styles = css `
    ${unsafeCSS(css_248z)}
  `;
__decorate([
    property({ type: Object })
], HTTradingViewSimpleChart.prototype, "data", void 0);
__decorate([
    property({ type: Boolean })
], HTTradingViewSimpleChart.prototype, "loading", void 0);
__decorate([
    property({ type: String, attribute: 'selected-period' })
], HTTradingViewSimpleChart.prototype, "selectedPeriod", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-volume' })
], HTTradingViewSimpleChart.prototype, "showVolume", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-controls' })
], HTTradingViewSimpleChart.prototype, "showControls", void 0);
__decorate([
    property({ type: Number, attribute: 'chart-height' })
], HTTradingViewSimpleChart.prototype, "chartHeight", void 0);
__decorate([
    property({ type: Object })
], HTTradingViewSimpleChart.prototype, "translations", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "chart", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "tooltip", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "candlestickSeries", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "volumeSeries", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "lineSeries", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "candlesticksEnabled", void 0);
__decorate([
    state()
], HTTradingViewSimpleChart.prototype, "volumeEnabled", void 0);
HTTradingViewSimpleChart = __decorate([
    customElement('ht-trading-view-simple-chart')
], HTTradingViewSimpleChart);
var HTTradingViewSimpleChart$1 = HTTradingViewSimpleChart;

export { HTTradingViewSimpleChart$1 as default };
//# sourceMappingURL=tradingViewSimpleChart.js.map
